import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Declarative D3 charts with React 16.3",
  "description": "",
  "published": "2018-04-27T00:00:00.000Z",
  "redirect_from": ["/blog/declarative-d3-charts-react-16-3/swizec/8353"],
  "categories": "d3, Front End, react, react+d3js, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The new React 16.3 brings some changes to the ecosystem that change how we go about integrating `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React and D3`}</a>{` to build data visualizations. I previously wrote about this in `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/declarative-d3-transitions-react/swizec/8323"
      }}>{`Declarative D3 transitions with React 16.3`}</a></p>
    <p><inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{`, `}<inlineCode parentName="p">{`componentWillUpdate`}</inlineCode>{` and `}<inlineCode parentName="p">{`componentWillMount`}</inlineCode>{` are on their way out. They were great for making `}<a parentName="p" {...{
        "href": "https://swizec.com/reactd3js"
      }}>{`React and D3`}</a>{` happy together, but they cause issues with async rendering that the React team is planning for React 17.`}</p>
    <p>{`You tend to use those now-deprecated lifecycle methods to update D3 objects’ internal state. Things like setting scale domains and ranges, updating complex D3 layouts, setting up transitions, etc.`}</p>
    <p>{`But you don’t need to! You can do it all with the new lifecycle API.`}</p>
    <p>{`Here’s a small example of building a bar chart with React 16.3. Using only approved lifecycle callbacks 😏`}</p>
    <lite-youtube {...{
      "videoid": "kH7bnSX9t4Q",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`You can play with it on CodeSandbox 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/lq0yr1lnq?module=%2Fsrc%2FBarChart.js",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <h2 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h2>
    <p>{`The core problem we're solving is that D3 objects like to keep internal state and React doesn't like that. We have to update D3 objects whenever our React component gets new props.`}</p>
    <p>{`You have to update a scale mapping data to x-axis pixels whenever either data or width change. Traditionally you would do that in `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{`.`}</p>
    <p>{`React docs recommend replacing `}<inlineCode parentName="p">{`componentWillReceiveProps`}</inlineCode>{` with `}<inlineCode parentName="p">{`componentDidUpdate`}</inlineCode>{`, but that leads to rendering stale charts. If you update your D3 scales `}<em parentName="p">{`after`}</em>{` your component re-renders, that's too late.`}</p>
    <p>{`Instead, we can move our scales into `}<inlineCode parentName="p">{`state`}</inlineCode>{` and use `}<inlineCode parentName="p">{`getDerivedStateFromProps`}</inlineCode>{`. 🤯`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/04/d3react16.3-699x1024.png",
        "alt": "d3 react 16.3"
      }}></img></p>
    <p>{`That's right, you can have complex objects in state now. It's totally safe.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`No that’s fine, React won’t change them, it’s one of the new “was bad practice but is fine now” things that comes with async. You can even mutate their properties.`}</p>{`— Ryan Florence (@ryanflorence) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/ryanflorence/status/984513942696296448"
      }}>{`April 12, 2018`}</a></blockquote>
    <p>{`Here's the gist of it 👇`}</p>
    <h3 {...{
      "id": "d3-in-state"
    }}>{`D3 in state`}</h3>
    <p>{`Defining D3 objects as component properties used to be best. Now you should do it in your component `}<inlineCode parentName="p">{`state`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{}}>{`class BarChart extends React.Component {
  state = {
    widthScale: d3
      .scaleBand()
      .domain(d3.range(0, this.props.data.length))
      .range([0, this.props.width]),

    heightScale: d3
      .scaleLinear()
      .domain([0, d3.max(this.props.data)])
      .range([0, this.props.height])
  };
`}</code></pre>
    <p>{`We define a `}<inlineCode parentName="p">{`widthScale`}</inlineCode>{` and a `}<inlineCode parentName="p">{`heightScale`}</inlineCode>{`. Each has a `}<inlineCode parentName="p">{`domain`}</inlineCode>{` and a `}<inlineCode parentName="p">{`range`}</inlineCode>{` that both depend on `}<inlineCode parentName="p">{`this.props`}</inlineCode>{`. Yes you can do that in `}<a parentName="p" {...{
        "href": "http://2ality.com/2017/07/class-fields.html"
      }}>{`JavaScript class fields`}</a>{` syntax.`}</p>
    <h3 {...{
      "id": "update-d3-in-getderivedstatefromprops"
    }}>{`Update D3 in getDerivedStateFromProps`}</h3>
    <p>{`You then use `}<inlineCode parentName="p">{`getDerivedStateFromProps`}</inlineCode>{` to keep those scales up to date when your component updates.`}</p>
    <pre><code parentName="pre" {...{}}>{`  static getDerivedStateFromProps(nextProps, prevState) {
    let { widthScale, heightScale } = prevState;

    widthScale.domain(d3.range(0, nextProps.data.length));
    heightScale.domain([0, d3.max(nextProps.data)]);

    prevState = { ...prevState, widthScale, heightScale };
    return prevState;
  }
`}</code></pre>
    <p>{`It's a static method, which means no `}<inlineCode parentName="p">{`this`}</inlineCode>{` keyword for you, you're running on the class, not an instance. You get the new props and the current state.`}</p>
    <p>{`Take `}<inlineCode parentName="p">{`widthScale`}</inlineCode>{` and `}<inlineCode parentName="p">{`heightScale`}</inlineCode>{` out of props, update their domains, put them back. Probably should update their ranges too.`}</p>
    <h3 {...{
      "id": "render-your-chart"
    }}>{`Render your chart`}</h3>
    <p>{`Now that your scales are always up to date, you can render your chart. Same as usual, D3 for props, React for rendering.`}</p>
    <pre><code parentName="pre" {...{}}>{`  render() {
    const { x, y, data, height } = this.props,
      { widthScale, heightScale } = this.state;

    return (
      <g transform="{\`translate(\${x}," \${y})\`}="">
        {data.map((d, i) => (
          <rect x={widthScale(i)} y="{height" -="" heightscale(d)}="" width={widthScale.bandwidth()} height={heightScale(d)}>
        ))}
      </rect></g>
    );
  }
`}</code></pre>
    <p>{`Get chart coordinates, `}<inlineCode parentName="p">{`data`}</inlineCode>{`, and `}<inlineCode parentName="p">{`height`}</inlineCode>{` from props. Grab `}<inlineCode parentName="p">{`widthScale`}</inlineCode>{` and `}<inlineCode parentName="p">{`heightScale`}</inlineCode>{`. Return a `}<inlineCode parentName="p">{`<g>`}</inlineCode>{` element full of rectangles.`}</p>
    <p>{`Each rectangle is rendered in a loop and takes its coordinates and dimensions from our scales.`}</p>
    <p>{`The result after a splash of color: A BarChart of random numbers where height and color correlate to the value. `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/lq0yr1lnq"
      }}><img parentName="a" {...{
          "src": "https://swizec.com/blog/wp-content/uploads/2018/04/chart-screenshot.png",
          "alt": "Simple chart in D3 and React 16.3"
        }}></img></a>{` I also made a button that lets you add random values to the chart. That way you can see that it's updating perfectly declaratively. Update props and the chart updates. No need to understand implementation details.`}</p>
    <p>{`You can `}<a parentName="p" {...{
        "href": "https://codesandbox.io/s/lq0yr1lnq"
      }}>{`play with it on CodeSandbox`}</a>{`.`}</p>
    <h2 {...{
      "id": "pushing-it-too-far"
    }}>{`Pushing it too far`}</h2>
    <p>{`You can render 100,000 SVG nodes in CodeSandbox if you're patient. Then you can't edit your code anymore.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`You can render 100,000 SVG elements in @codesandboxapp and then you can't edit your code anymore.`}<br parentName="p"></br><br parentName="p"></br>{`👉 `}<a parentName="p" {...{
          "href": "https://t.co/8IQCpZteAq"
        }}>{`https://t.co/8IQCpZteAq`}</a>{` `}<a parentName="p" {...{
          "href": "https://t.co/NRjp7iVWrE"
        }}>{`pic.twitter.com/NRjp7iVWrE`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/989909019463630848"
      }}>{`April 27, 2018`}</a></blockquote>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      